import React, { Suspense, useEffect, useRef, useState } from 'react';

import CssBaseline from '@material-ui/core/CssBaseline';
import { connect } from 'react-redux';
import { Redirect, Route, Router } from 'react-router';

import { ErrorBoundary, SetCookie } from '../App';
import * as BuildConstants from '../BuildConstants';
import { LoadingSpinner } from '../components/layouts';
import { analyticsService, flagService } from '../core/services';
import { logger } from '../core/services/loggerService';
import {
  activeLocaleInRedux,
  availableLanguages,
  initialLocale,
} from '../localization';
import { OrderCompleteProps } from '../pages/OrderComplete/OrderComplete';
import { StylesProvider } from '../style/StylesProvider';
import type { WebOrderTheme } from '../style/ThemeProvider';
import { ThemeProvider } from '../style/ThemeProvider';
import { domUtils } from '../utils';
import { NotificationsProvider } from '../utils/providers';
import * as Actions from './actions/Actions';
import { setupLanguage } from './actions/loadingActions';
import * as analytics from './analytics';
import * as Api from './Api';
import HttpsCheck from './HttpsCheck';
import * as Intercom from './intercom';
import { history } from './services';
import * as Settings from './SettingsStore';
import { setupAxios } from './setupAxios';
import { setupDOMandPolyfills } from './setupPolyfills';
import * as Store from './Store';
import { getWLID, Utilities } from './Utilities';

const Checkout = React.lazy(() => import('./component/Checkout'));
const CollectionList = React.lazy(() => import('./component/CollectionList'));
const CustomLocation = React.lazy(() => import('./component/CustomLocation'));
const Delivery = React.lazy(() => import('./component/Delivery'));
const DeliveryList = React.lazy(() => import('./component/DeliveryList'));
const Map = React.lazy(() => import('./component/Map'));
const MenuContainer = React.lazy(() => import('./component/MenuContainer'));
const NewLocation = React.lazy(() => import('./component/NewLocation'));
const OrderHistory = React.lazy(() => import('../pages/OrderHistory'));
const OrderHistoryDetail = React.lazy(
  () => import('../pages/OrderHistoryDetail'),
);
const OrderView = React.lazy(() => import('./component/OrderView'));
const Over18 = React.lazy(() => import('./component/Over18'));
const PhoneNumber = React.lazy(() => import('./component/PhoneNumber'));
const Restaurant = React.lazy(() => import('./component/Restaurant'));
const SmsCode = React.lazy(() => import('./component/SmsCode'));
const Unavailable = React.lazy(() => import('./component/Unavailable'));
const Where = React.lazy(() => import('./component/Where'));

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

type InnerProps = StateProps & DispatchProps;
type OuterProps = {
  children?: React.ReactNode;
  settings: any;
  store?: any;
};
type Props = InnerProps & OuterProps;

function Weborder(props: Props): JSX.Element {
  const {
    appConfig,
    children,
    deviceId,
    flagServiceStatus,
    loadCustomer,
    setAppDesignScheme,
    setTheme,
    settings,
    store = false,
    themeName,
    storeLocale,
  } = props;

  const [fontName, setFontName] = useState<string>('');
  const isFirstOrderViewMount = useRef(true);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(
      `Branch:${BuildConstants.GIT_BRANCH} Hash:${BuildConstants.GIT_HASH} Build No.:${BuildConstants.BUILD_NUM}`,
    );
  }, []);

  useEffect(() => {
    Utilities.setInLocalStorage(
      'onAppLoadUserSessionHistoryLength',
      window.history.length,
    );
  }, []);

  useEffect(() => {
    if (!store) {
      const userGeolocation = Settings.LocationStore.Load();
      setupAxios(settings, userGeolocation);
      setupDOMandPolyfills(settings);
      initialiseStore(userGeolocation);
    }
  }, []);

  useEffect(() => {
    if (settings.initialRoute) {
      history.replace(settings.initialRoute);
    }
  }, []);

  useEffect(() => {
    if (
      appConfig.status === 'success' &&
      appConfig.data?.ContactFormType === Api.ContactFormType.Intercom &&
      appConfig.data?.IntercomAppId
    ) {
      Intercom.Load(appConfig.data.IntercomAppId, appConfig.data.WhiteLabelId);
    }
  }, [appConfig.status]);

  useEffect(() => {
    const setupFeatureFlagService = (configData): void => {
      const storeInstance = Store.getStoreInstance();
      const featureFlagParams = {
        countryCode: configData.CountryCode,
        deviceId,
        whitelabelId: configData.WhiteLabelId,
      };
      flagService.initialise(storeInstance, featureFlagParams);
    };

    if (flagServiceStatus === 'NOT_READY' && appConfig.status === 'success') {
      setupFeatureFlagService(appConfig.data);
    }
  }, [appConfig.status, flagServiceStatus]);

  useEffect(() => {
    const setupApplicationTheme = (configData) => {
      const appDesignScheme = configData.AppDesignScheme;

      // Theme: Redesign2021 (working title 'Budapest')
      const primary = Utilities.returnColor(
        appDesignScheme.Primary,
        BuildConstants.PRIMARY_COLOR,
      );
      const secondary = Utilities.returnColor(
        appDesignScheme.Secondary,
        BuildConstants.THEME_COLOR,
      );
      const tertiary = Utilities.returnColor(
        appDesignScheme.Tertiary,
        BuildConstants.TERTIARY_COLOR,
      );

      const font = domUtils.loadFont(getWLID(), appDesignScheme.Font);
      setFontName(font);
      // ICH+2021.12.06 - CSS Position Sticky vs. Overflow
      // Hopefully some day soon this assertive change can be entirely
      // removed regardless of what styles are added in third-party websites.
      // https://www.designcise.com/web/tutorial/how-to-fix-issues-with-css-position-sticky-not-working#checking-if-an-ancestor-element-has-overflow-property-set
      domUtils.assertAncestorOverflowVisible();

      setAppDesignScheme({
        Font: font,
        Primary: primary,
        Secondary: secondary,
        Tertiary: tertiary,
      });
      setTheme('budapest');

      domUtils.addHtmlAttribute(
        '#flipdish-menu',
        'style',
        `--fdPrimaryColor: #${configData.AppDesignScheme.Primary};`,
      );
    };

    if (appConfig.status === 'success' && flagServiceStatus !== 'NOT_READY') {
      setupApplicationTheme(appConfig.data);
    }
  }, [appConfig.status, flagServiceStatus]);

  const initialiseStore = (loadedGeo) => {
    const {
      loadAppConfig,
      setLocation,
      loadPhoneNumberConfig,
      setupLocale,
      setOffset,
    } = props;

    if (settings.offset !== null && !Number.isNaN(parseInt(settings.offset))) {
      setOffset(parseInt(settings.offset));
    }

    const woStore = Store.getStoreInstance();
    // eslint-disable-next-line no-underscore-dangle
    (window as any)._flipdish = {
      setLanguage: (lang: string): void => {
        setLanguage(woStore.dispatch, lang);
      },
      getLanguage: (): string => {
        const currentLanguageInStorage = activeLocaleInRedux(
          woStore.getState().locale?.languages,
        );
        return currentLanguageInStorage;
      },
    };

    setupLocale(initialLocale());

    loadAppConfig();

    if (loadedGeo) {
      setLocation(loadedGeo);
    }

    const storeInstance = Store.getStoreInstance();
    Store.watchForLocationChanges(storeInstance, (geo) => {
      loadPhoneNumberConfig(geo);
    });
  };

  const setLanguage = (dispatch: any, lang: string): void => {
    /* eslint-disable no-underscore-dangle */
    if (
      (window as any)._flipdish &&
      (window as any)._flipdish.getLanguage &&
      (window as any)._flipdish.getLanguage() !== lang
    ) {
      if (availableLanguages.includes(lang)) {
        dispatch(Actions.ChangeLanguage(lang));
      } else {
        logger.error(`_flipdish.setLanguage failed`, {
          message: 'requested language not in available languages',
          requestedLang: lang,
        });
      }
    }
    /* eslint-enable no-underscore-dangle */
  };

  const logPageView = (): void => {
    analytics.PageView();
    analyticsService.pageView();

    if (store) {
      Intercom.Update(store.getState().intercom);
    }
    Utilities.setInLocalStorage('flipdish-last-url', window.location.hash);
  };

  if (
    appConfig.status !== 'success' ||
    flagServiceStatus === 'NOT_READY' ||
    !storeLocale.some((l) => l?.active) ||
    fontName === ''
  ) {
    return <LoadingSpinner />;
  }

  loadCustomer();

  const storeLastPhysicalRestaurantId = (restaurantId: string): void => {
    Utilities.storeLastPhysicalRestaurantId(
      appConfig.data.AppNameId,
      restaurantId,
    );
  };

  const orderViewComponent = (
    componentProps: OrderCompleteProps,
  ): JSX.Element => (
    <OrderView
      {...componentProps}
      isFirstMount={isFirstOrderViewMount.current}
    />
  );

  const base = settings.baseHref;
  const webOrderRoutes = (
    <Suspense fallback={<LoadingSpinner />}>
      <Router history={history}>
        <Route path={base} component={SetCookie}>
          <Route
            path="phone(/:dlid)"
            component={PhoneNumber}
            onEnter={(): void => logPageView()}
          />
          <Route
            path="code/:phone(/:dlid)"
            component={SmsCode}
            onEnter={(): void => logPageView()}
          />
          <Route
            path="order/:orderId(/:concessionStoreId)"
            component={(componentProps: OrderCompleteProps): JSX.Element =>
              orderViewComponent(componentProps)
            }
            onEnter={(): void => logPageView()}
            onLeave={(): void => {
              isFirstOrderViewMount.current = false;
            }}
          />
          <Route
            path="restaurant/:vrid"
            component={MenuContainer}
            onEnter={(): void => logPageView()}
          />
          <Route
            path="menu/:vrid(/:type/:id)"
            component={MenuContainer}
            onEnter={(): void => logPageView()}
          />
          <Route path="where" component={Where} onEnter={() => logPageView()} />
          <Route
            path="unavailable"
            component={Unavailable}
            onEnter={(): void => logPageView()}
          />
          <Route
            path="collection"
            component={CollectionList}
            onEnter={(): void => logPageView()}
          />
          <Route
            path="(restaurant/:vrid/)delivery"
            component={Delivery}
            onEnter={(): void => logPageView()}
          />
          <Route
            path="delivery/new"
            component={NewLocation}
            onEnter={(): void => logPageView()}
          />
          <Route
            path="delivery/map"
            component={Map}
            onEnter={(): void => logPageView()}
          />
          <Route
            path="delivery/custom/:lat/:lng"
            component={CustomLocation}
            onEnter={(): void => logPageView()}
          />
          <Route
            path="delivery/confirm(/:lat/:lng)(/:placeId)"
            component={CustomLocation}
            onEnter={(): void => logPageView()}
          />
          <Route
            path="delivery/:location"
            component={DeliveryList}
            onEnter={(): void => logPageView()}
          />
          <Route
            path="restaurant/:vrid/:type/:id(/:concessionStoreId)"
            component={Restaurant}
            onEnter={(route): void => {
              logPageView();
              storeLastPhysicalRestaurantId(route.params.id);
            }}
          />
          <Route
            path="over18/:vrid/:type/:id(/:concessionStoreId)"
            component={Over18}
            onEnter={(): void => logPageView()}
          />
          <Route
            path="checkout/:vrid/:type/:id(/:concessionStoreId)"
            component={Checkout}
            onEnter={(): void => logPageView()}
          />
          <Route
            path="history"
            component={OrderHistory}
            onEnter={(): void => logPageView()}
          />
          <Route
            path="history/:orderId"
            component={OrderHistoryDetail}
            onEnter={(): void => logPageView()}
          />
        </Route>
        <Redirect from={`${base}*`} to={`${base}where`} />
      </Router>
    </Suspense>
  );

  return (
    <ErrorBoundary identifier="app">
      <div id="flipdish-budapest">
        <StylesProvider namespace="fd-budapest">
          <ThemeProvider themeName={themeName} fontName={fontName}>
            <CssBaseline />

            <NotificationsProvider>
              <HttpsCheck>{children || webOrderRoutes}</HttpsCheck>
            </NotificationsProvider>
          </ThemeProvider>
        </StylesProvider>
      </div>
    </ErrorBoundary>
  );
}

const mapStateToProps = (state: Store.State) => ({
  appConfig: state.flipdish.AppConfig,
  deviceId: state.flipdish.Config.DeviceId,
  flagServiceStatus: flagService.getFlagServiceStatus(state),
  themeName: state.flipdish.themeName,
  storeLocale: state.locale.languages || [],
});

const mapDispatchToProps = (dispatch: any) => {
  const StoreLocationCertainlySettings = Api.LocationCertainty.Settings;

  return {
    loadAppConfig: () => {
      dispatch(Actions.LoadAppconfig() as any);
    },
    loadCustomer: () => {
      dispatch(Actions.LoadCustomer() as any);
    },
    loadPhoneNumberConfig: (geo: any) => {
      dispatch(Actions.LoadPhoneNumberConfig(geo) as any);
    },
    setAppDesignScheme: (appDesignScheme: any) => {
      dispatch(Actions.SetAppDesignScheme(appDesignScheme));
    },
    setupLocale: (localeCode: string): void => {
      dispatch(setupLanguage(localeCode));
    },
    setLocation: (loadedGeo: any) => {
      dispatch(Actions.AddLocation(loadedGeo, StoreLocationCertainlySettings));
    },
    setOffset: (offset: number) => {
      dispatch(Actions.SetOffset(offset));
    },
    setTheme: (theme: WebOrderTheme) => {
      dispatch(Actions.SetTheme(theme));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Weborder);
